<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-else-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <Refund
      v-else-if="hasOrder"
      v-bind="refundInfo"
      slot="middle"
    />
    <BaseForm
      v-else
      ref="form"
      v-bind="form"
      novalidate
      class="screen__form"
      @submit="onFormSubmit"
    />
  </BaseScreen>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import screenMixin from '@/mixins/screen';
import terminalMixin from '@/mixins/terminal';
import BaseForm from '@/components/BaseForm.vue';
import FieldOrderLink from '@/components/FieldOrderLink.vue';
import Refund from '@/components/Refund.vue';

import CONSTANTS from '@/helpers/constants';

const {
  SCREEN_READY,
  ACTION_RETRY,
  ACTION_SUBMIT,
} = CONSTANTS;

const REFUND_STATUS_DELAY = 3000; // Промежуток между проверками статуса возврата
const REFUND_STATUS_LIMIT = 100; // Кол-во попыток проверки

export default {
  name: 'RefundScreen',
  mixins: [screenMixin, terminalMixin],
  components: {
    BaseForm,
    Refund,
  },
  data() {
    return {
      errorCode: 0,
      errorMessage: '',
      statusAttemptsCount: 0,
      orderLink: '',
      orderSum: '',
      order: null,
      isRefundComplete: false,
    };
  },
  staticData: () => ({
    statusTimeout: null,
  }),
  computed: {
    ...mapState([
      'isAdmin',
    ]),
    title() {
      return this.$t('titles.refund');
    },
    error() {
      return {
        title: String(this.errorCode),
        text: this.errorMessage,
        actions: this.isAdmin ? [
          {
            text: this.$t('actions.backToMenu'),
            action: () => this.$router.push(this.$router.options.paths.admin),
          },
          {
            text: this.$t('actions.retry'),
            action: this.retryRefund,
          },
        ] : [
          {
            text: this.$t('actions.backToMenu'),
            action: () => this.$router.push(this.$router.options.paths.admin),
          },
        ],
      };
    },
    thanks() {
      return {
        icon: {
          name: 'success',
          isSuccess: true,
        },
        title: this.$t('titles.refundComplete'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
          {
            text: this.$t('actions.backToMenu'),
            action: () => this.$router.push(this.$router.options.paths.admin),
          },
        ],
      };
    },
    form() {
      return {
        fields: [
          {
            id: 'link',
            component: FieldOrderLink,
            defaultValue: this.orderLink,
          },
          {
            id: 'sum',
            field: {
              type: 'number',
              placeholder: this.$t('fields.ticketAmount'),
              errorMessage: this.$t('fields.badSum'),
              required: true,
            },
            defaultValue: this.orderSum,
          },
        ],
        submitButtonText: this.$t('actions.makeRefund'),
      };
    },
    bookingTickets() {
      return this.order?.tickets.filter((ticket) => (ticket.status === 'booking')) || [];
    },
    paymentSum() {
      return Number(this.orderSum);
      // return this.bookingTickets.reduce((sum, ticket) => (sum + ticket.price), 0);
    },
    paymentItems() {
      return this.bookingTickets.map(this.prepareSaleItem);
    },
    hasOrder() {
      return Boolean(this.order);
    },
    isStatusLimitReached() {
      return this.statusAttemptsCount >= REFUND_STATUS_LIMIT;
    },
    refundInfo() {
      return {
        order: this.order,
        tickets: this.paymentItems.length,
        sum: this.paymentSum,
      };
    },
  },
  mounted() {
    this.initActions();
    this.checkAdminPermissions();
  },
  methods: {
    ...mapActions('cart', [
      'fetchOrderByLink',
      'fetchRefundStatus',
      'startRefund',
      'cancelOrderById',
    ]),
    prepareSaleItem(item) {
      return {
        name: `${this.$t('fields.ticket')}: №${item.id}`,
        price: item.price,
        quantity: 1,
        sum: item.price,
        discount: 0,
        tax_type: 7,
      };
    },
    async checkRefundStatus() {
      try {
        const status = await this.fetchRefundStatus(this.orderLink);
        if (status !== '0') {
          this.statusAttemptsCount = REFUND_STATUS_LIMIT;
          throw new Error();
        }
        this.onRefundComplete();
      } catch {
        if (this.isStatusLimitReached) {
          this.order = null;
          this.statusAttemptsCount = 0;
          this.clearStatusTimeout();
          this.onTerminalError(500, this.$t('texts.refundFailed'));
        } else {
          this.setCheckStatusTimeout();
        }
      }
    },
    async onRefundComplete() {
      try {
        this.showPending();
        this.isRefundComplete = true;
        await this.cancelOrderById({
          id: this.order.id,
          isRefund: true,
        });
        this.startTerminalRefundRegistration();
      } catch (e) {
        console.error('onRefundComplete error: ', e);
        this.onTerminalError(500, this.$t('texts.cancelOrderFailed'));
      }
    },
    async onFormSubmit({ link, sum }) {
      if (this.isPending) return;
      try {
        this.showPending();
        this.orderLink = link;
        this.orderSum = sum;

        this.order = await this.fetchOrderByLink(link);

        await this.startRefund({
          orderId: this.order.id,
          orderLink: this.orderLink,
          orderSum: Number(this.orderSum),
        });

        this.hidePending();
        this.setCheckStatusTimeout();
      } catch (e) {
        console.error('onFormSubmit error: ', e);
        this.onTerminalError(500, this.$t('texts.refundFailed'));
      }
    },
    clearStatusTimeout() {
      window.clearTimeout(this.statusTimeout);
    },
    setCheckStatusTimeout() {
      this.clearStatusTimeout();
      this.statusTimeout = window.setTimeout(this.checkRefundStatus, REFUND_STATUS_DELAY);
      this.statusAttemptsCount += 1;
    },
    checkAdminPermissions() {
      if (this.isAdmin) return;
      this.onTerminalError(403, this.$t('fields.wrongPassword'));
    },
    onTerminalError(code, message) {
      this.errorCode = code;
      this.errorMessage = message;
      this.showError();
    },
    onTerminalPaperOut() {
      this.onTerminalError(50, this.$t('titles.paperOut'));
    },
    onRefundRegistrationComplete() {
      this.showThanks();
    },
    retryRefund() {
      if (!this.isErrorVisible) return;
      this.hideError();
      if (this.isRefundComplete) {
        this.onRefundComplete();
      }
    },
    setFieldFocus() {
      return this.$refs.form?.setFocus();
    },
    submitForm() {
      return this.$refs.form?.submit();
    },
    initActions() {
      const rootActions = [
        [SCREEN_READY, this.setFieldFocus],
        [ACTION_RETRY, this.retryRefund],
        [ACTION_SUBMIT, this.submitForm],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        this.clearStatusTimeout();
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>
